/**
 * Error mapping — every failed API call, whichever client made it, becomes one `ApiProblem`, and
 * one function decides how it is shown (hand-off COMPONENTS.md "Error and permission mapping").
 *
 * The API answers `{ error, message, retryable, docs_url, details?, requestId? }`
 * (shared/utils/src/api-errors.ts). It reaches the browser three ways:
 *   - the SDK's `InferrexHttpError`: `.status` + `.body` (the envelope);
 *   - the api-client's hand-written `apiFetch`: an Error with `.status`, `.code`, `.body`;
 *   - no answer at all: a fetch TypeError (offline, DNS, CORS) or the SDK's timeout.
 * Structural reads, not `instanceof`, so a second copy of a class in the bundle cannot break it.
 *
 * Rules: show `message`; offer retry only when `retryable`; a failure with no answer is
 * retryable (nothing was said to the contrary, and nothing could have been applied twice — every
 * write carries an Idempotency-Key).
 */
import type { StateBlockKind } from './StateBlock';

export interface ApiProblem {
  /** HTTP status, or 0 when the request got no answer. */
  readonly status: number;
  /** The API's stable code (`validation_error`, `forbidden`, …), or `network_error` / `timeout`. */
  readonly code: string;
  /** Plain-language message for a person. Always set. */
  readonly message: string;
  readonly retryable: boolean;
  readonly docsUrl?: string | undefined;
  readonly requestId?: string | undefined;
  readonly details?: unknown;
}

export const FALLBACK_MESSAGE = 'Something went wrong on our side.';
const NETWORK_MESSAGE = 'We couldn’t reach Inferrex. Check your connection and try again.';
const TIMEOUT_MESSAGE = 'Inferrex took too long to answer. Try again in a moment.';

interface Envelope {
  error?: unknown;
  message?: unknown;
  retryable?: unknown;
  docs_url?: unknown;
  requestId?: unknown;
  details?: unknown;
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v : undefined;
}

/** Normalise anything a query or mutation threw. */
export function toApiProblem(err: unknown): ApiProblem {
  if (err && typeof err === 'object') {
    const e = err as { name?: unknown; status?: unknown; code?: unknown; body?: unknown; message?: unknown };
    if (e.name === 'InferrexTimeoutError') {
      return { status: 0, code: 'timeout', message: TIMEOUT_MESSAGE, retryable: true };
    }
    if (typeof e.status === 'number' && e.status > 0) {
      const body: Envelope = e.body && typeof e.body === 'object' ? e.body as Envelope : {};
      const code = str(body.error) ?? str(e.code) ?? `http_${e.status}`;
      // 5xx with no explicit flag: the server failed, not the request — trying again can work.
      const retryable = typeof body.retryable === 'boolean' ? body.retryable : e.status >= 500 || e.status === 429;
      return {
        status: e.status,
        code,
        message: str(body.message) ?? str(e.message) ?? FALLBACK_MESSAGE,
        retryable,
        docsUrl: str(body.docs_url),
        requestId: str(body.requestId),
        details: body.details,
      };
    }
    if (e.name === 'TypeError' || e.name === 'AbortError') {
      return { status: 0, code: 'network_error', message: NETWORK_MESSAGE, retryable: true };
    }
    if (str(e.message)) return { status: 0, code: 'client_error', message: String(e.message), retryable: false };
  }
  return { status: 0, code: 'client_error', message: FALLBACK_MESSAGE, retryable: false };
}

/** How a problem is shown. */
export type ProblemPresentation =
  | { readonly as: 'state'; readonly kind: StateBlockKind; readonly title?: string; readonly message?: string }
  | { readonly as: 'fields'; readonly fields: readonly FieldProblem[]; readonly message: string };

export interface FieldProblem {
  readonly path: string;
  readonly message: string;
}

const SUBSCRIPTION_CODES = new Set(['subscription_required', 'subscription_inactive', 'payment_required']);
const FORBIDDEN_CODES = new Set(['forbidden', 'scope_denied', 'insufficient_permissions', 'permission_denied']);

/** `details` of a 400 validation_error, as field paths a form can attach messages to. */
export function fieldProblems(problem: ApiProblem): FieldProblem[] {
  if (!Array.isArray(problem.details)) return [];
  const out: FieldProblem[] = [];
  for (const d of problem.details as unknown[]) {
    if (!d || typeof d !== 'object') continue;
    const item = d as { path?: unknown; message?: unknown };
    const path = Array.isArray(item.path) ? item.path.map(String).join('.') : typeof item.path === 'string' ? item.path : '';
    if (typeof item.message === 'string') out.push({ path, message: item.message });
  }
  return out;
}

/**
 * Where a failed page load lands (COMPONENTS.md table):
 *   403 forbidden / scope_denied     → StateBlock forbidden
 *   403 subscription_required        → StateBlock subscription (plus the shell banner)
 *   404 not_found                    → StateBlock empty: removed, or not in this environment
 *   400 validation_error with fields → field-level errors (a form)
 *   anything else                    → StateBlock error with the server's message
 */
export function presentProblem(problem: ApiProblem): ProblemPresentation {
  if (problem.status === 402 || SUBSCRIPTION_CODES.has(problem.code)) return { as: 'state', kind: 'subscription' };
  // Every other 403 (forbidden, scope_denied, a role the route rejects) reads the same to a person.
  if (problem.status === 403 || FORBIDDEN_CODES.has(problem.code)) return { as: 'state', kind: 'forbidden' };
  if (problem.status === 404) {
    return { as: 'state', kind: 'empty', title: 'Not found', message: 'This was removed or isn’t in this environment.' };
  }
  if (problem.status === 400 && problem.code === 'validation_error') {
    const fields = fieldProblems(problem);
    if (fields.length) return { as: 'fields', fields, message: problem.message };
  }
  return { as: 'state', kind: 'error', message: problem.message };
}

/** Is this the in-flight / reused-key conflict a disabled button prevents? */
export function isIdempotencyConflict(problem: ApiProblem): boolean {
  return problem.status === 409 && (problem.code === 'request_in_progress' || problem.code === 'idempotency_key_reused');
}
